import type { ApiListResponse } from './response';

type PagerRunner<T> = (page: number) => Promise<ApiListResponse<T>>;

export class TablePager<T = any> {
  visible = true;
  loading = false;
  initial = true;
  initialItems: number;
  list: ApiListResponse<T> | null = null;
  items: T[] = [];
  total: number | null = null;
  page = 1;
  pages: number | null = null;
  hasMore = false;
  stored: Record<number, ApiListResponse<T>> = {};
  runner: PagerRunner<T>;

  constructor(runner: PagerRunner<T>, initialItems = 3) {
    this.initialItems = initialItems;
    this.runner = runner;
  }

  async load(page = 1) {
    this.loading = true;

    try {
      const list = await this.fetch(page);

      if (list) {
        if (list.paging) {
          this.total = list.paging.itemCount;
          this.page = list.paging.page;
          this.pages = list.paging.pageCount;
        }

        this.list = list;
        this.setItems(list);

        if (list.paging) {
          this.hasMore = (list.paging.pageCount > list.paging.page) || (this.initial && this.visibleItems.length < this.items.length);
        }
      }
    } finally {
      this.loading = false;
    }
  }

  async fetch(page = 1): Promise<ApiListResponse<T> | null> {
    const list = this.stored[page] || await this.runner(page);

    if (list) {
      this.stored[page] = list;
    }

    return list;
  }

  setItems(list: ApiListResponse<T>) {
    this.items = list.items;
  }

  reload() {
    this.items = [];
    this.stored = {};
    this.page = 1;

    return this.load(1);
  }

  get visibleItems(): T[] {
    if (this.initial) {
      return this.items.slice(0, this.initialItems);
    } else {
      return this.items;
    }
  }
}

export class EndlessListPager<T = any> extends TablePager<T> {
  constructor(runner: PagerRunner<T>, initialItems = 3) {
    super(runner, initialItems);
  }

  override async fetch(page = 1): Promise<ApiListResponse<T> | null> {
    return await this.runner(page);
  }

  override setItems(list: ApiListResponse<T>) {
    if (this.page === 1) {
      this.items = list.items;
    } else {
      this.items.push(...list.items);
    }
  }

  loadMore() {
    if (this.initial) {
      this.initial = false;
      this.hasMore = ((this.pages || 0) > this.page);
    } else {
      this.load(this.page + 1);
    }
  }
}
